// src/services/api/endpoints/spaces.ts
import { Space, Area, Gathering } from '../../../types';
import { API_BASE_URL, fetchWithTokenRefresh } from '../client';
import { 
  generateCacheKey,
  getCacheItem,
  setCacheItem
} from '../cache';
import { PaginatedResponse } from '../utils';

/**
 * Fetches all spaces from the Fabrik API
 * @param token Firebase authentication token
 * @returns Promise containing an array of spaces
 */
export const fetchSpaces = async (token: string): Promise<Space[]> => {
  try {
    const url = `${API_BASE_URL}/spaces/search`;
    const cacheKey = generateCacheKey(url);
    
    // Check if we have a valid cached response
    const cachedData = getCacheItem<Space[]>(cacheKey);
    if (cachedData) {
      return cachedData;
    }
    
    // fetchWithTokenRefresh already returns the parsed JSON data
    const data = await fetchWithTokenRefresh(url, token);
    
    // Cache the response
    setCacheItem(cacheKey, data.items);
    
    return data.items;
  } catch (error) {
    console.error('Error fetching spaces:', error);
    throw error;
  }
};

/**
 * Fetches a space by its ID
 * @param token Firebase authentication token
 * @param spaceId The ID of the space to fetch
 * @returns Promise containing the space
 */
export const fetchSpaceById = async (token: string, spaceId: string): Promise<Space> => {
  try {
    const url = `${API_BASE_URL}/spaces/${spaceId}`;
    const cacheKey = generateCacheKey(url);
    
    // Check if we have a valid cached response
    const cachedData = getCacheItem<Space>(cacheKey);
    if (cachedData) {
      return cachedData;
    }
    
    // fetchWithTokenRefresh already returns the parsed JSON data
    const data = await fetchWithTokenRefresh(url, token);
    
    // Cache the response
    setCacheItem(cacheKey, data);
    
    return data;
  } catch (error) {
    console.error('Error fetching space:', error);
    throw error;
  }
};

/**
 * Fetches a specific area by ID
 * @param token Firebase authentication token
 * @param areaId The ID of the area to fetch
 * @returns Promise containing the area details
 */
export const fetchAreaById = async (token: string, areaId: string): Promise<Area> => {
  try {
    const url = `${API_BASE_URL}/areas/${areaId}`;
    const cacheKey = generateCacheKey(url);
    
    // Check if we have a valid cached response
    const cachedData = getCacheItem<Area>(cacheKey);
    if (cachedData) {
      return cachedData;
    }
    
    // fetchWithTokenRefresh already returns the parsed JSON data
    const data = await fetchWithTokenRefresh(url, token);
    
    // Cache the response
    setCacheItem(cacheKey, data);
    
    return data;
  } catch (error) {
    console.error('Error fetching area:', error);
    throw error;
  }
};

/**
 * Fetches all areas for a specific space
 * @param token Firebase authentication token
 * @param spaceId The ID of the space to fetch areas for
 * @returns Promise containing an array of areas
 */
export const fetchAreasBySpaceId = async (token: string, spaceId: string): Promise<Area[]> => {
  try {
    const searchParams = new URLSearchParams();
    searchParams.append('space_id', spaceId);
    
    const url = `${API_BASE_URL}/areas/search?${searchParams.toString()}`;
    const cacheKey = generateCacheKey(url, { space_id: spaceId });
    
    // Check if we have a valid cached response
    const cachedData = getCacheItem<Area[]>(cacheKey);
    if (cachedData) {
      return cachedData;
    }
    
    // fetchWithTokenRefresh already returns the parsed JSON data
    const data = await fetchWithTokenRefresh(url, token);
    
    // Cache the response
    setCacheItem(cacheKey, data.items);
    
    return data.items;
  } catch (error) {
    console.error('Error fetching areas for space:', error);
    throw error;
  }
};

/**
 * Fetches gatherings by date and space
 * @param token Firebase authentication token
 * @param fromDate Start date in YYYY-MM-DD format
 * @param toDate End date in YYYY-MM-DD format
 * @param spaceId Optional space_id filter
 * @returns Promise containing an array of gatherings
 */
export const fetchGatheringsByDateAndSpace = async (
  token: string,
  fromDate: string,
  toDate: string,
  spaceId: string | null
): Promise<Gathering[]> => {
  try {
    const searchParams = new URLSearchParams();
    searchParams.append('from_date', fromDate);
    searchParams.append('to_date', toDate);
    if (spaceId) searchParams.append('space_id', spaceId);

    const url = `${API_BASE_URL}/gatherings/search?${searchParams.toString()}`;
    const cacheKey = generateCacheKey(url, { fromDate, toDate, spaceId });
    
    // Check if we have a valid cached response
    const cachedData = getCacheItem<Gathering[]>(cacheKey);
    if (cachedData) {
      return cachedData;
    }
    
    // fetchWithTokenRefresh already returns the parsed JSON data
    const data = await fetchWithTokenRefresh(url, token);
    
    // Cache the response
    setCacheItem(cacheKey, data.items);
    
    return data.items;
  } catch (error) {
    console.error('Error fetching gatherings by date and space:', error);
    throw error;
  }
};
